import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsUsageOf, capabilitiesLost, capabilityOf, meetsPlan, minPlanFor, seatLimit, type CapabilityId } from '@dale/plans';

import { ApiError } from '../api/client';
import { changeMembershipPlan, getMembership, MEMBERSHIP_QUERY_KEY } from '../api/endpoints/membership';
import type { Membership } from '../api/schemas';
import { useSession } from '../auth/useSession';
import { BillingCycleToggle } from '../components/BillingCycleToggle';
import { CancelPlan } from '../components/CancelPlan';
import { APPLE_BILLING_QUERY_KEY, getAppleBilling, type AppleBilling } from '../api/endpoints/apple';
import { ConfirmAction } from '../components/ConfirmAction';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { MembershipPayment } from '../components/MembershipPayment';
import { PageHeader } from '../components/PageHeader';
import { PlanComparison } from '../components/PlanComparison';
import { formatWholeDollars, PlanPicker } from '../components/PlanPicker';
import { formatDateOnly, formatMoneyCents } from '../lib/format';
import {
  annualDiscountPctOf,
  annualPriceOf,
  cyclePriceText as cyclePrice,
  monthlyPriceOf as priceOf,
} from '../lib/membership-price';
import { modeForCategory } from '../lib/mode';
import {
  effectiveCycle,
  PLAN_ORDER,
  planHasAnnualBilling,
  planLabel,
  twelveMonthsCents,
  type BillingCycle,
  type PlanTier,
} from '../lib/plan';
import { useBusiness } from '../lib/useBusiness';

type MembershipChange = { plan: PlanTier; billingCycle: BillingCycle; planMoved: boolean };
type Confirmation = { membership: Membership; planMoved: boolean };

/**
 * The owner's own Dale plan: what they pay, what it includes, and a way to
 * change it. `initialPlan` comes from a lock's "Cambiar a Pro": that plan
 * starts picked, so confirming is the only step left.
 */
export function MembresiaPage({ initialPlan }: { initialPlan?: PlanTier } = {}) {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const membershipQuery = useQuery({ queryKey: MEMBERSHIP_QUERY_KEY, queryFn: getMembership });
  const membership = membershipQuery.data;
  const appleQuery = useQuery({ queryKey: APPLE_BILLING_QUERY_KEY, queryFn: getAppleBilling });
  const apple = appleQuery.data?.collectedBy === 'apple' ? appleQuery.data : undefined;
  const [planChoice, setPlanChoice] = useState<PlanTier | undefined>(initialPlan);
  const [cycleChoice, setCycleChoice] = useState<BillingCycle | undefined>();
  const [confirmation, setConfirmation] = useState<Confirmation | undefined>();

  const mutation = useMutation({
    mutationFn: ({ plan, billingCycle }: MembershipChange) => changeMembershipPlan(plan, billingCycle),
    onSuccess: (next, change) => {
      queryClient.setQueryData(MEMBERSHIP_QUERY_KEY, next);
      void queryClient.invalidateQueries({ queryKey: ['business-detail', session?.businessId] });
      setConfirmation({ membership: next, planMoved: change.planMoved });
      setPlanChoice(undefined);
      setCycleChoice(undefined);
    },
  });

  const business = useBusiness();
  const restaurant = business ? modeForCategory(business.category) === 'restaurant' : false;
  const selectedPlan = planChoice ?? membership?.plan;
  const selectedCycle = cycleChoice ?? membership?.billingCycle ?? 'monthly';

  return (
    <main className="shell-main">
      <PageHeader title="Tu membresía" description="Tu plan en Dale y lo que incluye. Lo cambias cuando quieras." />
      {membershipQuery.isLoading ? <LoadingState label="Cargando tu plan…" /> : null}
      {membershipQuery.isError ? (
        <>
          <ErrorBanner message={errorMessage(membershipQuery.error)} />
          <button type="button" className="btn small secondary" onClick={() => void membershipQuery.refetch()}>
            Reintentar
          </button>
        </>
      ) : null}
      {membership && selectedPlan ? (
        <>
          <MembershipNow membership={membership} />
          {apple ? (
            <PaidWithApple apple={apple} />
          ) : (
            <>
              <MembershipPayment />
              <section className="section" aria-label="Planes">
                <h2 className="section-label">Planes</h2>
                <BillingCycleToggle
                  value={selectedCycle}
                  onChange={(cycle) => {
                    setCycleChoice(cycle);
                    setConfirmation(undefined);
                  }}
                  discountPct={annualDiscountPctOf(membership)}
                  disabled={mutation.isPending}
                />
                <PlanPicker
                  name="membresia"
                  value={selectedPlan}
                  onChange={(plan) => {
                    setPlanChoice(plan);
                    setConfirmation(undefined);
                  }}
                  current={membership.plan}
                  currentLabel="Tu plan"
                  priceFor={(plan) => priceOf(membership, plan)}
                  cycle={selectedCycle}
                  annualPriceFor={(plan) => annualPriceOf(membership, plan)}
                  offered={membership.plans.map((entry) => entry.id)}
                  disabled={mutation.isPending}
                />
                <ChangeActions
                  membership={membership}
                  plan={selectedPlan}
                  cycle={selectedCycle}
                  restaurant={restaurant}
                  pending={mutation.isPending}
                  onConfirm={(change) => mutation.mutate(change)}
                />
                {mutation.isError ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
                {confirmation ? (
                  <p className="row-sub" role="status">
                    {confirmationLine(confirmation)}
                  </p>
                ) : null}
              </section>
              <details className="plan-compare-wrap">
                <summary>Compara qué incluye cada plan</summary>
                <PlanComparison
                  current={membership.plan}
                  selected={selectedPlan}
                  restaurant={restaurant}
                  priceFor={(plan) => perMonth(membership, plan, selectedCycle)}
                  onChoose={(plan) => {
                    setPlanChoice(plan);
                    setConfirmation(undefined);
                  }}
                />
              </details>
              <CancelPlan />
            </>
          )}
        </>
      ) : null}
    </main>
  );
}

const APPLE_PLAN_NAMES: Record<NonNullable<AppleBilling['plan']>, string> = { base: 'Base', pro: 'Pro', max: 'Max', ultra: 'Ultra' };

/** A plan bought in the Dale Negocios iPhone app: Apple renews it and only Apple changes or cancels it. */
function PaidWithApple({ apple }: { apple: AppleBilling }) {
  const name = apple.plan ? APPLE_PLAN_NAMES[apple.plan] : 'tu plan';
  const renews = apple.expiresAt ? new Date(apple.expiresAt).toLocaleDateString('es-PA', { day: 'numeric', month: 'long' }) : null;
  return (
    <section className="section" aria-label="Pago con la App Store">
      <h2 className="section-label">Pago</h2>
      <p>
        Pagas tu plan con la App Store{renews ? `: ${name} se renueva el ${renews}` : ''}. Para cambiarlo o cancelarlo, abre Dale Negocios
        en tu iPhone, en Más, Tu plan, o en Ajustes, tu nombre, Suscripciones. Apple maneja los cobros y los reembolsos.
      </p>
    </section>
  );
}

type ChangeActionsProps = {
  membership: Membership;
  plan: PlanTier;
  cycle: BillingCycle;
  restaurant: boolean;
  pending: boolean;
  onConfirm: (change: MembershipChange) => void;
};

/** What confirming would change, and the two-step button that does it. */
function ChangeActions({ membership, plan, cycle, restaurant, pending, onConfirm }: ChangeActionsProps) {
  const billingCycle = effectiveCycle(plan, cycle);
  const planMoved = plan !== membership.plan;
  const cycleMoved = billingCycle !== membership.billingCycle;
  const label = actionLabel(plan, billingCycle, planMoved);
  return (
    <div className="membership-actions">
      <p className="row-sub">{changeHint(membership, plan, cycle, restaurant)}</p>
      {planMoved || cycleMoved ? (
        <ConfirmAction
          label={label}
          confirmLabel={`Sí, ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
          pendingLabel="Cambiando…"
          pending={pending}
          onConfirm={() => onConfirm({ plan, billingCycle, planMoved })}
        />
      ) : null}
    </div>
  );
}

function MembershipNow({ membership }: { membership: Membership }) {
  const discount = Math.round(membership.discountPct);
  const yearly = membership.billingCycle === 'annual' ? annualPriceOf(membership, membership.plan) : null;
  const since = membership.planChangedAt ? `Desde el ${formatDateOnly(membership.planChangedAt.slice(0, 10))}` : undefined;
  const sub = [
    since,
    discount > 0 ? `${discount}% de descuento acordado` : undefined,
    yearly === null
      ? undefined
      : `Pago anual, ahorras ${formatWholeDollars(twelveMonthsCents(membership.monthlyPriceCents) - yearly)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="membership-now">
      <p className="membership-now-plan">{planLabel(membership.plan)}</p>
      <p className="membership-now-price">
        <strong>{formatWholeDollars(yearly ?? membership.monthlyPriceCents)}</strong>
        <span>{yearly === null ? monthlyPer(membership) : 'al año'}</span>
      </p>
      <p className="membership-now-sub">{sub || 'Tu plan actual en Dale.'}</p>
    </div>
  );
}

/** A plan's price read per month on the cycle picked: the year up front divided by twelve. */
function perMonth(membership: Membership, plan: PlanTier, cycle: BillingCycle): number {
  const yearly = effectiveCycle(plan, cycle) === 'annual' ? annualPriceOf(membership, plan) : null;
  return yearly === null ? priceOf(membership, plan) : Math.round(yearly / 12);
}

function monthlyPer(membership: Membership): string {
  if (membership.plan !== 'reservas') return 'al mes';
  return `al mes, más ${formatMoneyCents(membership.perBookingCents)} por reserva`;
}

function actionLabel(plan: PlanTier, cycle: BillingCycle, planMoved: boolean): string {
  if (planMoved) return `Cambiar a ${planLabel(plan)}${cycle === 'annual' ? ' anual' : ''}`;
  return cycle === 'annual' ? 'Pasar a pago anual' : 'Pasar a pago mensual';
}

/** What changes if the owner confirms: gains going up, losses going down, the cycle's price. */
function changeHint(membership: Membership, plan: PlanTier, cycle: BillingCycle, restaurant: boolean): string {
  const billingCycle = effectiveCycle(plan, cycle);
  if (plan !== membership.plan) return planMoveHint(membership, plan, billingCycle, restaurant);
  if (billingCycle === membership.billingCycle) {
    return cycle === 'annual' && !planHasAnnualBilling(plan)
      ? 'Independientes se paga por reserva, así que no tiene pago anual.'
      : 'Escoge otro plan o forma de pago para ver qué cambia.';
  }
  if (billingCycle === 'monthly') {
    return `Vuelves a pagar ${formatWholeDollars(priceOf(membership, plan))} al mes, sin el ${annualDiscountPctOf(membership)}% del pago anual.`;
  }
  const yearly = annualPriceOf(membership, plan) ?? 0;
  const twelveMonths = twelveMonthsCents(priceOf(membership, plan));
  return `Pagas ${formatWholeDollars(yearly)} al año en vez de ${formatWholeDollars(twelveMonths)}. Ahorras ${formatWholeDollars(twelveMonths - yearly)}.`;
}

/** A section's name inside a sentence: its first letter lowered, except a brand like Dale Agents. */
function inSentence(title: string): string {
  return title.startsWith('Dale ') ? title : `${title.charAt(0).toLowerCase()}${title.slice(1)}`;
}

/** Comandas and the Menú exist for restaurants only, so nobody else is told they lose them. */
const RESTAURANT_ONLY: ReadonlySet<CapabilityId> = new Set(['comandas', 'menu']);

function planMoveHint(membership: Membership, plan: PlanTier, cycle: BillingCycle, restaurant: boolean): string {
  const from = PLAN_ORDER.indexOf(membership.plan);
  const to = PLAN_ORDER.indexOf(plan);
  if (to > from) {
    return `Se activa al instante. Desde tu próxima factura pagas ${cyclePrice(membership, plan, cycle)}.`;
  }
  const lost = capabilitiesLost(membership.plan, plan)
    .filter((id) => restaurant || !RESTAURANT_ONLY.has(id))
    .map((id) => inSentence(capabilityOf(id).title));
  const seats = `Pasas de ${seatLimit(membership.plan)} a ${seatLimit(plan)} ${seatLimit(plan) === 1 ? 'acceso' : 'accesos'}.`;
  // Ultra to Max keeps every section: only the Dale Agents use and the logins go down.
  const sections = lost.length > 0 ? `Dejas de ver: ${lost.join(', ')}.` : 'No dejas de ver nada.';
  const keepsAgents = meetsPlan(plan, minPlanFor('agentes'));
  const usage = keepsAgents && agentsUsageOf(plan) < agentsUsageOf(membership.plan) ? ` Dale Agents queda con el uso de ${planLabel(plan)}.` : '';
  return `${sections}${usage} ${seats} Tus datos se guardan y vuelven si subes otra vez.`;
}

function confirmationLine({ membership, planMoved }: Confirmation): string {
  if (planMoved) return `Listo. Ya estás en ${planLabel(membership.plan)} y el menú se actualizó.`;
  return `Listo. Ahora pagas ${cyclePrice(membership, membership.plan, membership.billingCycle)}.`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cambiar tu plan ahora mismo. Intenta de nuevo en un momento.';
}
